import React, { type FC, type MouseEventHandler, useEffect, useState } from 'react';

import cn from 'classnames';

import { Button } from '@shared/ui/button';
import IconDialPad from '@assets/img/icons/keyboard-phone.svg';

import { DIAL_PAD_KEYS } from './constants';
import { getDialPadSuggestionTitle } from './lib/suggestion-title';
import { type DialPadTarget, type IDialPadProps } from './types';

import styles from './styles.module.scss';

/*
  DialPad – поле ввода + клавиши + кнопка вызова
*/
export const DialPad: FC<IDialPadProps> = ({
	onClick,
	defaultOpenKeys = true,
	disabledCall,
	svgButton,
	textButton,
	classNames,
	initialValue,
	showInputButton = false,
	suggestions,
	onQueryChange,
}) => {
	const [value, setValue] = useState<string>(initialValue || '');
	const [showKeys, setShowKeys] = useState<boolean>(defaultOpenKeys);

	useEffect(() => {
		setShowKeys(defaultOpenKeys);
	}, [defaultOpenKeys]);

	const handleKeyPress =
		(key: string): MouseEventHandler<HTMLButtonElement> =>
		() =>
			setValue((v) => {
				const nextValue = v + key;
				onQueryChange?.(nextValue);
				return nextValue;
			});

	const handleChangeInput: React.ChangeEventHandler<HTMLInputElement> = (event) => {
		const nextValue = event.target.value;
		setValue(nextValue);
		onQueryChange?.(nextValue);
	};

	const handleClick = (withVideo: boolean) => () => {
		const rawInput = value.trim();
		if (!rawInput) return;

		onClick({ aor: rawInput, displayName: rawInput, withVideo });
		onQueryChange?.('');
	};

	const handleSuggestionMouseDown =
		(target: DialPadTarget): MouseEventHandler<HTMLButtonElement> =>
		(event) => {
			event.preventDefault();
			setValue(target.aor);
			onQueryChange?.('');
		};

	const handleClickInputClick = () => {
		setShowKeys((s) => !s);
	};

	const normalizedInput = value.trim();
	const shouldShowSuggestions = normalizedInput.length > 0 && Boolean(suggestions?.length);

	return (
		<div className={cn(styles.Wrapper, classNames)}>
			{/* Поле ввода */}
			<div className={styles.InputWrapper}>
				<input
					className={styles.Input}
					placeholder="Введите ID, имя или телефон"
					type="text"
					value={value}
					onChange={handleChangeInput}
				/>
				{showInputButton && (
					<Button
						buttonType="icon-border"
						className={cn(styles.InputIcon, { [styles.active]: showKeys })}
						svg={IconDialPad}
						onClick={handleClickInputClick}
					/>
				)}

				{shouldShowSuggestions && (
					<div className={styles.Suggestions}>
						{suggestions?.map((item) => (
							<button
								key={`${item.aor}|${item.displayName || ''}`}
								className={styles.SuggestionButton}
								type="button"
								onMouseDown={handleSuggestionMouseDown(item)}
							>
								<span className={styles.SuggestionTitle}>{getDialPadSuggestionTitle(item)}</span>
								<span className={styles.SuggestionSubtitle}>{item.aor}</span>
							</button>
						))}
					</div>
				)}
			</div>

			{/* Сетка клавиш */}
			{showKeys && (
				<div className={styles.Grid}>
					{DIAL_PAD_KEYS.map(({ label, letters }) => (
						<Button key={label} className={styles.Key} onClick={handleKeyPress(label)}>
							<span className={styles.KeyLabel}>{label}</span>
							{letters && <span className={styles.KeyLetters}>{letters}</span>}
						</Button>
					))}
				</div>
			)}

			{/* Кнопка «Позвонить» */}
			<Button
				buttonType="primary"
				className={styles.CallBtn}
				disabled={normalizedInput.length === 0 || disabledCall}
				svg={svgButton}
				svgPosition="left"
				onClick={handleClick(false)}
			>
				{textButton}
			</Button>
			{/* <Button */}
			{/*    disabled={value.length === 0} */}
			{/*    onClick={handleClick(true)} */}
			{/*    buttonType={'primary'} */}
			{/*    className={styles.CallBtn} */}
			{/* > */}
			{/*    Позвонить видео */}
			{/* </Button> */}
		</div>
	);
};
